import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useRef,
} from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { UserContext } from "../../../UserContext";
import toast from "react-hot-toast";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaBox,
  FaSearch,
  FaSeedling,
  FaBarcode,
  FaLeaf,
  FaMapMarkerAlt,
} from "react-icons/fa";
import "./style.css";

// Product Card Component
const ProductCard = ({ product, onEdit, onDelete, onClick, isArabic, t }) => {
  const [showActions, setShowActions] = useState(false);

  const getDisplayName = () => {
    return isArabic
      ? product.nameAr || product.nameEn
      : product.nameEn || product.nameAr;
  };

  const getTypeName = () => {
    if (product.inventoryType) {
      return isArabic
        ? product.inventoryType.nameAr || product.inventoryType.nameEn
        : product.inventoryType.nameEn || product.inventoryType.nameAr;
    }
    return t("General");
  };

  const getLocationName = () => {
    if (product.inventoryLocation) {
      return isArabic
        ? product.inventoryLocation.nameAr || product.inventoryLocation.nameEn
        : product.inventoryLocation.nameEn || product.inventoryLocation.nameAr;
    }
    return "";
  };

  const getDescription = () => {
    // Backend uses a single 'details' field for description
    const description = product.details || "";

    // Debug log to see if descriptions exist
    if (description) {
      console.log(`Product "${product.nameEn || product.nameAr}" has description:`, description);
    }

    return description;
  };

  return (
    <div
      className="product-card"
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
      onClick={() => onClick(product)}
    >
      <div className="product-card-header">
        <div className="product-icon-wrapper">
          {product.isCrop ? <FaLeaf /> : <FaBox />}
        </div>
        <div className={`product-actions ${showActions ? "visible" : ""}`}>
          <button
            className="action-btn edit-btn"
            onClick={(e) => {
              e.stopPropagation();
              onEdit(product);
            }}
            title={t("Edit")}
          >
            <FaEdit />
          </button>
          <button
            className="action-btn delete-btn"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(product);
            }}
            title={t("Delete")}
          >
            <FaTrash />
          </button>
        </div>
      </div>

      <div className="product-card-body">
        <div className="product-main-info">
          <h3 className="product-name" title={getDisplayName()}>
            {getDisplayName()}
          </h3>
          <span className="product-type-badge">{getTypeName()}</span>
        </div>

        <div className="product-details">
          {product.registrationNumber && (
            <div className="detail-item">
              <FaBarcode className="detail-icon" />
              <span className="detail-text">{product.registrationNumber}</span>
            </div>
          )}

          {getLocationName() && (
            <div className="detail-item">
              <FaMapMarkerAlt className="detail-icon" />
              <span className="detail-text">{getLocationName()}</span>
            </div>
          )}
        </div>

        {getDescription() && (
          <div className="product-description">
            <p className="description-text">{getDescription()}</p>
          </div>
        )}

        <div className="product-balance-info-section">
          <div className="balance-info-label">
            {t("Current Stock")}
          </div>
          <div className="balance-info-display">
            <span className="balance-info-number">
              {product.currentBalance || 0}
            </span>
            <span className="balance-info-unit">{product.unit || t("unit")}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Delete Confirmation Modal
const DeleteConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  itemName,
  t,
}) => {
  if (!isOpen) return null;

  return (
    <div className="modal-container">
      <div className="modal-box">
        <h2 className="modal-title">{t("Confirm Deletion")}</h2>
        <p className="modal-confirmation-text">
          {t("Are you sure you want to delete")} <strong>"{itemName}"</strong>?{" "}
          {t("This action cannot be undone.")}
        </p>
        <div className="modal-buttons">
          <button className="button-stock-products-secondary" onClick={onClose}>
            {t("Cancel")}
          </button>
          <button className="button-stock-products-danger" onClick={onConfirm}>
            {t("Delete")}
          </button>
        </div>
      </div>
    </div>
  );
};

// Main StockProducts Component
const StockProducts = () => {
  const navigate = useNavigate();
  const [t, i18n] = useTranslation();
  const { token, userData } = useContext(UserContext);

  // State management
  const [activeTab, setActiveTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    product: null,
  });

  const searchInputRef = useRef(null);
  const farmId = userData.Employee?.farmId;
  const farmType = userData.Employee?.farmType || "soil";

  // Fetch products or crops based on active tab
  const fetchData = useCallback(async () => {
    if (!farmId) return;

    setLoading(true);
    try {
      let endpoint;
      if (activeTab === "products") {
        endpoint = `api/inventoryItemDetails/getInventoryProductsWithCurrentBalance/${farmId}`;
      } else {
        // For crops, add farmType parameter
        endpoint = `api/inventoryItemDetails/getFarmHarvestsWithCurrentBalance/${farmId}?farmType=${farmType}`;
      }

      const response = await axios.get(endpoint, {
        headers: { Authorization: token },
      });

      // The API returns data wrapped in an object with items array
      let items = response.data?.items || [];

      // The backend already filters for items with current balance > 0, no need to filter here

      // Log products for debugging
      if (activeTab === "products") {
        console.log("=== PRODUCT LIST ===");
        console.log(`Total products fetched: ${items.length}`);
        items.forEach((item, index) => {
          console.log(`Product ${index + 1}:`, {
            id: item.id,
            nameAr: item.nameAr,
            nameEn: item.nameEn,
            details: item.details,
            registrationNumber: item.registrationNumber,
            currentBalance: item.currentBalance,
            unit: item.unit
          });
        });
        console.log("========================");
      }

      // Mark crops for icon display
      if (activeTab === "crops") {
        items = items.map((item) => ({ ...item, isCrop: true }));

        // Log harvest/crop list for debugging
        console.log("=== HARVEST/CROP LIST ===");
        console.log(`Total crops fetched: ${items.length}`);
        items.forEach((item, index) => {
          console.log(`Crop ${index + 1}:`, {
            id: item.id,
            nameAr: item.nameAr,
            nameEn: item.nameEn,
            details: item.details,
            fieldId: item.fieldId,
            typeId: item.typeId,
            locationId: item.locationId,
            currentBalance: item.currentBalance,
            unit: item.unit
          });
        });
        console.log("========================");
      }

      setProducts(items);
      setFilteredProducts(items);
    } catch (error) {
      console.error(`Error fetching ${activeTab}:`, error);
      toast.error(t(`Failed to load ${activeTab}.`));
      setProducts([]);
      setFilteredProducts([]);
    } finally {
      setLoading(false);
    }
  }, [activeTab, farmId, farmType, token, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Search functionality - search by product name and description
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredProducts(products);
      return;
    }

    const query = searchQuery.toLowerCase().trim();
    const filtered = products.filter((product) => {
      // Search in product names (Arabic and English)
      const nameAr = (product.nameAr || "").toLowerCase();
      const nameEn = (product.nameEn || "").toLowerCase();

      // Search in product details/description
      const details = (product.details || "").toLowerCase();

      return (
        nameAr.includes(query) ||
        nameEn.includes(query) ||
        details.includes(query)
      );
    });

    setFilteredProducts(filtered);
  }, [searchQuery, products]);

  // Handle product click - navigate to movements/details
  const handleProductClick = (product) => {
    // Navigate to product movements/details page
    navigate(`/inventory/product/${product.id}/transactions`, {
      state: { product },
    });
  };

  // Handle edit
  const handleEdit = (product) => {
    if (activeTab === "products") {
      navigate("/add-product", {
        state: {
          product,
          editMode: true,
          checkboxChecked: true,
        },
      });
    } else {
      navigate("/inventory/stock-products/add-edit-crop", {
        state: {
          crop: product,
          editMode: true,
        },
      });
    }
  };

  // Handle delete
  const handleDeleteClick = (product) => {
    setDeleteModal({
      isOpen: true,
      product,
    });
  };

  const handleDeleteConfirm = async () => {
    const { product } = deleteModal;
    if (!product) return;

    try {
      await axios.delete(`api/inventoryItemDetails/${product.id}`, {
        headers: { Authorization: token },
      });

      toast.success(
        t(
          `${
            activeTab === "products" ? "Product" : "Crop"
          } deleted successfully`
        )
      );
      fetchData(); // Refresh data
      setDeleteModal({ isOpen: false, product: null });
    } catch (error) {
      console.error("Error deleting item:", error);
      toast.error(t("Failed to delete item"));
    }
  };

  // Handle add button clicks
  const handleAddProduct = () => {
    navigate("/add-product", { state: { checkboxChecked: true } });
  };

  const handleAddCrop = () => {
    navigate("/inventory/stock-products/add-edit-crop");
  };

  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchQuery("");
  };

  // Get display name for delete modal
  const getProductDisplayName = (product) => {
    if (!product) return "";
    return i18n.language === "ar"
      ? product.nameAr || product.nameEn
      : product.nameEn || product.nameAr;
  };

  return (
    <div className="stock-products-container">
      {/* Header Section */}
      <header className="stock-header">
        <div className="header-left">
          <h1 className="header-title">{t("Stock Products")}</h1>
          <p className="header-subtitle">
            {filteredProducts.length}{" "}
            {activeTab === "products" ? t("products") : t("crops")}{" "}
            {t("available")}
          </p>
        </div>

        <div className="header-actions">
          <button
            className="button-stock-products-primary"
            onClick={handleAddProduct}
          >
            <FaPlus /> {t("Add Product")}
          </button>
          <button
            className="button-stock-products-secondary"
            onClick={handleAddCrop}
          >
            <FaSeedling /> {t("Add Crop")}
          </button>
        </div>
      </header>

      {/* Controls Section */}
      <div className="controls-section">
        {/* Tab Selector */}
        <div className="tab-selector">
          <button
            className={`tab-button ${activeTab === "products" ? "active" : ""}`}
            onClick={() => handleTabChange("products")}
          >
            <FaBox /> {t("Products")}
          </button>
          <button
            className={`tab-button ${activeTab === "crops" ? "active" : ""}`}
            onClick={() => handleTabChange("crops")}
          >
            <FaSeedling /> {t("Crops")}
          </button>
        </div>

        {/* Search Bar */}
        <div className="search-container">
          <FaSearch className="search-icon" />
          <input
            ref={searchInputRef}
            type="text"
            className="search-input"
            placeholder={t(`Search by name or description...`)}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {/* Content Section */}
      <div className="content-section">
        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner">{t("Loading...")}</div>
          </div>
        ) : filteredProducts.length > 0 ? (
          <div className="products-grid">
            {filteredProducts.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                onEdit={handleEdit}
                onDelete={handleDeleteClick}
                onClick={handleProductClick}
                isArabic={i18n.language === "ar"}
                t={t}
              />
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <div className="empty-icon">
              {activeTab === "products" ? <FaBox /> : <FaSeedling />}
            </div>
            <h3>{t(`No ${activeTab} found`)}</h3>
            <p className="empty-message">
              {searchQuery
                ? t(`No ${activeTab} match your search`)
                : t(
                    `Add your first ${
                      activeTab === "products" ? "product" : "crop"
                    } to get started`
                  )}
            </p>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, product: null })}
        onConfirm={handleDeleteConfirm}
        itemName={getProductDisplayName(deleteModal.product)}
        t={t}
      />
    </div>
  );
};

export default StockProducts;
